import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, DeleteConfirmationModal, FiltersButton, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useEmailDomains, useEmailDomainMutations } from '../hooks';
import { CreateDomainDialog } from '../components/CreateDomainDialog';
import { EmailDomainCard } from '../components/EmailDomainCard';
import { EmailDomainListItem } from '../components/EmailDomainListItem';
import type { EmailDomainResponse } from '@troia-v3/types';

type ViewMode = 'table' | 'grid';
type SortKey = 'domain' | 'status' | 'createdAt';
type StatusFilter = EmailDomainResponse['status'];
type CapabilityFilter = 'sending' | 'receiving';

const PAGE_SIZE = 12;

const STATUS_LABELS: Record<StatusFilter, string> = {
  verified: 'Verificado',
  pending: 'Pendente',
  partially_verified: 'Parcialmente verificado',
  failed: 'Falhou',
  not_started: 'Não iniciado',
};

const STATUS_RANK: Record<StatusFilter, number> = {
  verified: 0,
  partially_verified: 1,
  pending: 2,
  not_started: 3,
  failed: 4,
};

const STATUS_OPTIONS: [StatusFilter | undefined, string][] = [
  [undefined, 'Todos'],
  ['verified', 'Verificado'],
  ['partially_verified', 'Parcialmente verificado'],
  ['pending', 'Pendente'],
  ['not_started', 'Não iniciado'],
  ['failed', 'Falhou'],
];

const CAP_OPTIONS: [CapabilityFilter | undefined, string][] = [
  [undefined, 'Todas'],
  ['sending', 'Envio habilitado'],
  ['receiving', 'Recebimento habilitado'],
];

/**
 * Página de listagem de domínios de e-mail — padrão DS de listagem (toolbar 3 zonas,
 * filterbar de chips, bulk bar, view Lista/Cards, mobile = cards).
 *
 * @example
 * ```tsx
 * <Route path="settings/email-domains" element={<EmailDomainsPage />} />
 * ```
 */
export const EmailDomainsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: domains, isLoading, error } = useEmailDomains();
  const { deleteDomain, verifyDomain } = useEmailDomainMutations();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EmailDomainResponse | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'domain', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter | undefined>(undefined);
  const [capabilityFilter, setCapabilityFilter] = useState<CapabilityFilter | undefined>(undefined);

  const allDomains = useMemo(() => domains ?? [], [domains]);

  // Busca + filtros client-side (a API retorna a lista completa).
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return allDomains.filter((d) => {
      if (term && !d.domain.toLowerCase().includes(term) && !(d.region ?? '').toLowerCase().includes(term)) return false;
      if (statusFilter && d.status !== statusFilter) return false;
      if (capabilityFilter === 'sending' && !d.sendingEnabled) return false;
      if (capabilityFilter === 'receiving' && !d.receivingEnabled) return false;
      return true;
    });
  }, [allDomains, searchTerm, statusFilter, capabilityFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'domain') cmp = a.domain.localeCompare(b.domain, 'pt-BR');
      else if (sort.key === 'status') cmp = STATUS_RANK[a.status] - STATUS_RANK[b.status];
      else cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(
    () => sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sorted, currentPage],
  );

  const selectedCount = selectedIds.size;
  const allOnPageSelected = pageItems.length > 0 && pageItems.every((d) => selectedIds.has(d.id));
  const someOnPageSelected = pageItems.some((d) => selectedIds.has(d.id));

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const applyStatusFilter = (status?: StatusFilter) => {
    setStatusFilter(status);
    setPage(1);
    setFiltersOpen(false);
  };

  const applyCapabilityFilter = (cap?: CapabilityFilter) => {
    setCapabilityFilter(cap);
    setPage(1);
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setStatusFilter(undefined);
    setCapabilityFilter(undefined);
    setPage(1);
    setFiltersOpen(false);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageItems.forEach((d) => next.delete(d.id));
      else pageItems.forEach((d) => next.add(d.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const handleView = useCallback((id: string) => navigate(`/settings/email-domains/${id}`), [navigate]);
  const handleVerify = useCallback((id: string) => verifyDomain.mutate(id), [verifyDomain]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteDomain.mutateAsync(deleteTarget.id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(deleteTarget.id);
        return next;
      });
      setDeleteTarget(null);
    } catch {
      // Erro tratado pelo React Query
    }
  }, [deleteTarget, deleteDomain]);

  const bulkVerify = () => {
    allDomains
      .filter((d) => selectedIds.has(d.id) && d.status !== 'verified')
      .forEach((d) => verifyDomain.mutate(d.id));
    clearSelection();
  };

  const handleBulkDeleteConfirm = useCallback(async () => {
    await Promise.allSettled(Array.from(selectedIds).map((id) => deleteDomain.mutateAsync(id)));
    clearSelection();
    setBulkDeleteOpen(false);
  }, [selectedIds, deleteDomain]);

  const activeFilters: { key: string; label: string; onRemove: () => void }[] = [];
  if (statusFilter) activeFilters.push({ key: 'status', label: `Verificação: ${STATUS_LABELS[statusFilter]}`, onRemove: () => applyStatusFilter(undefined) });
  if (capabilityFilter) activeFilters.push({ key: 'cap', label: `Capacidade: ${capabilityFilter === 'sending' ? 'Envio' : 'Recebimento'}`, onRemove: () => applyCapabilityFilter(undefined) });
  const activeFilterCount = activeFilters.length;

  const renderCheck = (on: boolean, mixed: boolean, onClick: () => void, label: string) => (
    <button
      type="button"
      onClick={onClick}
      className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors flex-shrink-0"
      style={{
        backgroundColor: on || mixed ? 'var(--theme-primary)' : 'transparent',
        borderColor: on || mixed ? 'var(--theme-primary)' : 'var(--theme-input-border)',
        color: '#ffffff',
      }}
      aria-label={label}
      data-testid="domain-select-all"
    >
      {on && <Icon name="check" className="w-3 h-3" />}
      {!on && mixed && <span className="w-2 h-0.5 rounded-full bg-white" />}
    </button>
  );

  const SortHead: React.FC<{ label: string; k: SortKey }> = ({ label, k }) => (
    <th
      className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap cursor-pointer select-none"
      style={{ color: 'var(--theme-muted-text)' }}
      onClick={() => handleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && <Icon name={sort.dir === 'asc' ? 'chevron-up' : 'chevron-down'} className="w-3 h-3" />}
      </span>
    </th>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={<>
          {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <button onClick={() => navigate('/settings')} className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }}>
              Configurações
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Domínios de e-mail</span>
          </nav>
          <span
            className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
            style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
          >
            {allDomains.length}
          </span>
        </>}
        center={
          /* Centro: busca (sempre centralizada) — ToolbarSearch shared (F17-F22) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar domínio…"
            data-testid="domains-search"
          />
        }
        right={<>
          {/* Direita: view toggle + Filtros + Adicionar */}
          {/* View toggle */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="domains-view"
          />

          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              onClick={() => setFiltersOpen((v) => !v)}
              count={activeFilterCount}
              active={filtersOpen}
              data-testid="domains-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                >
                  <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Verificação</p>
                  {STATUS_OPTIONS.map(([val, label]) => {
                    const active = statusFilter === val;
                    return (
                      <button
                        key={label}
                        onClick={() => applyStatusFilter(val)}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                        style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        data-testid={`domains-filter-status-${val ?? 'all'}`}
                      >
                        <span className="truncate">{label}</span>
                        {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}

                  <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Capacidade</p>
                  {CAP_OPTIONS.map(([val, label]) => {
                    const active = capabilityFilter === val;
                    return (
                      <button
                        key={label}
                        onClick={() => applyCapabilityFilter(val)}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                        style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        data-testid={`domains-filter-cap-${val ?? 'all'}`}
                      >
                        <span className="truncate">{label}</span>
                        {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Adicionar domínio (único primário) — Button shared 38px (F01) */}
          <Button
            variant="primary"
            size="md"
            icon="plus"
            collapsibleLabel
            className="flex-none"
            onClick={() => setIsCreateOpen(true)}
            data-testid="new-domain"
          >
            Adicionar domínio
          </Button>
        </>}
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {activeFilters.map((f) => (
            <span key={f.key} className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              {f.label}
              <button onClick={f.onRemove} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label={`Remover ${f.label}`} data-testid={`domains-chip-remove-${f.key}`}>
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="domains-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionado(s)</span>
          <span className="flex-1" />
          <button onClick={bulkVerify} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }} data-testid="domains-bulk-verify">
            <Icon name="refresh" className="w-3.5 h-3.5" /> Reverificar
          </button>
          <button onClick={() => setBulkDeleteOpen(true)} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="domains-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="domains-bulk-clear">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar domínios: {(error as Error).message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando domínios…" />}

          {!isLoading && !error && sorted.length === 0 && (
            <ListPageEmpty
              icon="globe"
              message="Nenhum domínio cadastrado"
              description={searchTerm || activeFilterCount > 0
                ? 'Tente ajustar a busca ou os filtros.'
                : 'Adicione um domínio para começar a enviar e receber e-mails pela plataforma. Será necessário configurar os registros DNS.'}
              actionLabel="Adicionar domínio"
              onAction={() => setIsCreateOpen(true)}
            />
          )}

          {/* Grid (Cards) */}
          {!isLoading && !error && sorted.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {pageItems.map((domain) => (
                <EmailDomainCard
                  key={domain.id}
                  domain={domain}
                  verifying={verifyDomain.isPending}
                  onView={handleView}
                  onVerify={handleVerify}
                  onDelete={setDeleteTarget}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && sorted.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {pageItems.map((domain) => (
                  <EmailDomainCard
                    key={domain.id}
                    domain={domain}
                    verifying={verifyDomain.isPending}
                    onView={handleView}
                    onVerify={handleVerify}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <th className="pl-4 pr-1 py-3 w-11">
                        {renderCheck(allOnPageSelected, someOnPageSelected && !allOnPageSelected, toggleSelectAllOnPage, 'Selecionar todos')}
                      </th>
                      <SortHead label="Domínio" k="domain" />
                      <SortHead label="Verificação" k="status" />
                      <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Registros DNS</th>
                      <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Capacidades</th>
                      <SortHead label="Adicionado" k="createdAt" />
                      <th className="px-4 py-3 w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((domain) => (
                      <EmailDomainListItem
                        key={domain.id}
                        domain={domain}
                        selected={selectedIds.has(domain.id)}
                        verifying={verifyDomain.isPending}
                        onToggleSelect={toggleSelect}
                        onView={handleView}
                        onVerify={handleVerify}
                        onDelete={setDeleteTarget}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação */}
          {!isLoading && !error && totalPages > 1 && (
            <ListPagePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>

      {/* Create Domain Dialog */}
      <CreateDomainDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={(id) => navigate(`/settings/email-domains/${id}`)}
      />

      {/* Delete Confirmation Modal (single) */}
      <DeleteConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        message={`Tem certeza que deseja excluir o domínio "${deleteTarget?.domain}"?`}
        warningMessage="Todos os registros DNS associados serão removidos e e-mails neste domínio deixarão de funcionar."
        isLoading={deleteDomain.isPending}
      />

      {/* Bulk Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
        message={`Tem certeza que deseja excluir ${selectedCount} domínio(s) selecionado(s)?`}
        warningMessage="Todos os registros DNS associados serão removidos e e-mails nesses domínios deixarão de funcionar."
        isLoading={deleteDomain.isPending}
      />
    </div>
  );
};

EmailDomainsPage.displayName = 'EmailDomainsPage';
